import React from 'react';
import { Button, Flex, Typography, theme } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';

import KpiStatLabel from './KpiStatLabel';
import KpiStatPartitionBar, { type KpiStatSegment } from './KpiStatPartitionBar';
import { TrendWithPeriod } from './TrendIndicator';

export type { KpiStatSegment };

export type KpiStatCta = {
  label: string;
  /** Real href so middle-click and copy-link work; `onClick` does the SPA hop. */
  href: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

export type KpiStatProps = {
  id: string;
  label: string;
  info: string;
  /** `null` renders an em-dash — the section's request-error state. */
  value: number | null;
  change?: number;
  invertSentiment?: boolean;
  caption?: string;
  cta?: KpiStatCta;
  valueFontSize?: number;
  accent?: string;
  segments?: KpiStatSegment[];
};

/**
 * One stat tile in the KPI partition card, ported from the Moonshot prototype's
 * `KpiStat` (SearchPerformancePage.tsx).
 *
 * Row spacing comes from this Flex's `gap` alone — the pieces carry no margins
 * of their own, so the stack stays even as parts appear and disappear.
 *
 * A `null` value suppresses everything derived from it: share caption, partition
 * bar, trend chip and CTA. Enforced here rather than at the call site so the tile
 * can't contradict itself — the prototype passes the em-dash but keeps rendering
 * live percentages and trends beside it, and its CTA leads back to the same
 * failed view.
 */
function KpiStat({
  id,
  label,
  info,
  value,
  change,
  invertSentiment,
  caption,
  cta,
  valueFontSize,
  accent,
  segments,
}: KpiStatProps) {
  const { token } = theme.useToken();

  const fontSize = valueFontSize ?? token.fontSizeHeading3;
  const hasValue = value !== null;

  return (
    <Flex vertical gap={token.marginXXS} style={{ minWidth: 0 }}>
      <KpiStatLabel id={id} label={label} info={info} accent={accent} />

      <Flex align="baseline" wrap gap={token.marginXXS}>
        <span
          style={{
            fontSize,
            fontWeight: token.fontWeightStrong,
            lineHeight: token.lineHeightHeading3,
            color: token.colorText,
          }}
        >
          {hasValue ? value.toLocaleString() : '—'}
        </span>
        {hasValue && change !== undefined && <TrendWithPeriod change={change} invertSentiment={invertSentiment} />}
      </Flex>

      {hasValue && segments && <KpiStatPartitionBar segments={segments} />}

      {hasValue && caption && (
        <Typography.Text
          style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, color: token.colorTextSecondary }}
        >
          {caption}
        </Typography.Text>
      )}

      {/* `marginTop: auto` pins the CTA to the bottom so links line up across
          stats whose labels and captions wrap to different heights. */}
      {hasValue && cta && (
        <div style={{ marginTop: 'auto', paddingTop: token.paddingXXS }}>
          <Button
            type="link"
            size="small"
            href={cta.href}
            onClick={cta.onClick}
            style={{ padding: 0, height: 'auto', fontSize: token.fontSizeSM }}
          >
            <ArrowRightOutlined style={{ marginRight: token.marginXXS }} />
            {cta.label}
          </Button>
        </div>
      )}
    </Flex>
  );
}

export default KpiStat;
